import axios, { AxiosError } from 'axios';
import { 
  ScheduledCall, 
  BatchScheduleRequest, 
  ScheduleResponse, 
  ScheduledCallsResponse,
  CancelResponse,
  ApiError
} from '../types';
import { authService } from './auth';

// Replace with your actual API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-dev1.yib.io/api/v1';

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = authService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle authentication errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    console.log('API error intercepted:', error.response?.status, error.message);
    
    // Handle both 401 Unauthorized and 403 Forbidden errors when we haven't already tried to refresh
    if (error.response && 
        (error.response.status === 401 || error.response.status === 403) && 
        !originalRequest._retry) {
      
      console.log('Auth error detected, attempting to refresh token');
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const refreshSuccess = await authService.refreshToken();
        console.log('Token refresh result:', refreshSuccess);
        
        if (refreshSuccess) {
          // Update the auth header with the new token
          const newToken = authService.getToken();
          console.log('Got new token, updating request and retrying');
          
          // Make sure the config exists and has headers
          if (!originalRequest.headers) {
            originalRequest.headers = {};
          }
          
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Retry the original request with the new token
          return apiClient(originalRequest);
        } else {
          console.log('Token refresh failed, logging out');
          // If refresh failed, logout user
          authService.logout();
          return Promise.reject(new Error('Session expired. Please log in again.'));
        }
      } catch (refreshError) {
        console.error('Error during token refresh:', refreshError);
        authService.logout();
        return Promise.reject(new Error('Authentication error occurred. Please log in again.'));
      }
    }
    
    return Promise.reject(error);
  }
);

// Define error response type
interface ErrorResponse {
  message?: string;
  [key: string]: any;
}

// Handle API errors
const handleApiError = (error: AxiosError): ApiError => {
  console.log('handleApiError called with:', error);
  
  if (error.response) {
    // Type assertion for error.response.data
    const errorData = error.response.data as ErrorResponse;
    console.log('Error response data:', errorData);
    
    return {
      message: errorData.message || 'An error occurred',
      status: error.response.status
    };
  }
  
  return {
    message: error.message || 'Network error',
    status: 500
  };
};

// Call scheduling API functions
export const callSchedulingApi = {
  // Schedule a single call
  scheduleCall: async (call: ScheduledCall): Promise<ScheduleResponse> => {
    try {
      const response = await apiClient.post('/synth/agent/schedule-calls', call);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  // Schedule multiple calls
  scheduleBatchCalls: async (batchRequest: BatchScheduleRequest): Promise<ScheduleResponse> => {
    try {
      const response = await apiClient.post('/synth/agent/schedule-calls', batchRequest);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  // Get scheduled calls with optional filters
  getScheduledCalls: async (
    filters?: {
      status?: 'pending' | 'attempted' | 'placed' | 'failed';
      from_dt?: string;
      to_dt?: string;
      agent_id?: number | null;
    }
  ): Promise<ScheduledCallsResponse> => {
    try {
      console.log('API getScheduledCalls called with filters:', filters);
      const response = await apiClient.get('/synth/agent/scheduled-calls', {
        params: filters
      });
      console.log('Raw API response:', response);
      
      // Extract the scheduled calls from the correct location in the response
      const responseData = response.data;
      
      // Check if the data is in the expected format
      if (responseData.results && responseData.results.scheduled_calls) {
        // Return in the expected format for the component
        return {
          scheduled_calls: responseData.results.scheduled_calls,
          total: responseData.results.total || responseData.results.scheduled_calls.length
        };
      }
      
      // Fallback to the original structure if the expected format is not found
      return responseData;
    } catch (error) {
      console.error('API getScheduledCalls error:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Cancel a scheduled call
  cancelScheduledCall: async (callId: string): Promise<CancelResponse> => {
    try {
      const response = await apiClient.delete(`/synth/agent/scheduled-calls/${callId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  // Cancel multiple scheduled calls
  batchCancelScheduledCalls: async (callIds: string[]): Promise<CancelResponse> => {
    try {
      const response = await apiClient.post('/synth/agent/scheduled-calls/batch-cancel', {
        call_ids: callIds
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  }
};

// Add caching for trainerApi.getAgents to avoid multiple network requests per page load
let cachedAgents: any[] | undefined;
let cachedAgentsPromise: Promise<any[]> | undefined;

// Added new trainerApi for fetching agents with caching
export const trainerApi = {
  getAgents: async (): Promise<any[]> => {
    if (cachedAgents !== undefined) {
      return Promise.resolve(cachedAgents);
    }
    if (cachedAgentsPromise !== undefined) {
      return cachedAgentsPromise;
    }

    cachedAgentsPromise = apiClient.get('/synth_trainer?page=1&page_size=20&search=')
       .then(response => {
          cachedAgents = response.data.results || [];
          cachedAgentsPromise = undefined;
          return cachedAgents;
       })
       .catch((error) => {
          cachedAgentsPromise = undefined;
          throw handleApiError(error as AxiosError);
       }) as Promise<any[]>;
    return cachedAgentsPromise!;
  }
};

// Add internal channels API
export const internalChannelsApi = {
  getInternalChannels: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/internal-channels');
      return response.data.results;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  }
}; 